/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { randomId } from '@/utils/random';

export interface Toast {
  id: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  duration?: number;
  onClick?: () => void;
}

interface ToastContextValue {
  toasts: Toast[];
  showToast: (toast: Omit<Toast, 'id'>) => void;
  dismissToast: (id: string) => void;
  clearAllToasts: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

function filterOutToastById(prev: Toast[], id: string): Toast[] {
  return prev.filter((t) => t.id !== id);
}

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Track toast timeouts to prevent memory leaks
  const toastTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Cleanup all timeouts on unmount
  useEffect(() => {
    const timeouts = toastTimeoutsRef.current;
    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
      timeouts.clear();
    };
  }, []);

  const dismissToast = useCallback((id: string) => {
    // Clear the timeout for this toast if it exists
    const timeout = toastTimeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      toastTimeoutsRef.current.delete(id);
    }
    setToasts((prev) => filterOutToastById(prev, id));
  }, []);

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = randomId('toast');
    const newToast: Toast = { ...toast, id };

    setToasts((prev) => [...prev, newToast]);

    // Auto-dismiss after duration (default 4 seconds)
    const duration = toast.duration ?? 4000;
    if (duration > 0) {
      const dismissAfterDuration = () => {
        toastTimeoutsRef.current.delete(id);
        setToasts((prev) => filterOutToastById(prev, id));
      };
      const timeout = setTimeout(dismissAfterDuration, duration);
      toastTimeoutsRef.current.set(id, timeout);
    }
  }, []);

  const clearAllToasts = useCallback(() => {
    // Clear all timeouts when clearing all toasts
    toastTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    toastTimeoutsRef.current.clear();
    setToasts([]);
  }, []);

  const value = useMemo(
    () => ({
      toasts,
      showToast,
      dismissToast,
      clearAllToasts,
    }),
    [toasts, showToast, dismissToast, clearAllToasts]
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
